import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type ModuleDefault<T> = { default: T };

/**
 * Lazy-load a route chunk with one automatic reload if the chunk failed to fetch
 * (common after deploys when a stale service worker or browser cache is involved).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<ModuleDefault<T>>,
  moduleName?: string
): LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = 'chunk-reload-attempted';
    const hasReloaded = sessionStorage.getItem(storageKey) === '1';

    try {
      const module = await factory();
      sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      const label = moduleName || 'route chunk';
      console.warn(`[lazyWithRetry] Failed to load ${label}:`, error);

      if (!hasReloaded) {
        sessionStorage.setItem(storageKey, '1');
        window.location.reload();
        return { default: (() => null) as unknown as T };
      }

      sessionStorage.removeItem(storageKey);
      throw error;
    }
  });
}
